import styles from './Security.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import React from 'react';
import TitleSectionBack from '../../../Common/TitleSectionBack';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import TextField from '../../../Common/TextField';
import { authActions } from '../../store';
import CollapsibleCard from '../../../Common/CollapsibleCard';
import { getActiveSalon, getSalonsRaw } from '../../store/selectors';
import { shouldShowMultiSalonPasswordNotice } from '../../../../utils/businessToken';
import useWindowSize from '../../../../utils/useWindowSize';
import clx from 'classnames';
import { toastActions } from 'Containers/Toasts/store';
import { AlertTriangle, KeyRound } from 'lucide-react';

const fieldProps = {
  component: TextField,
  height: '50px',
  type: 'password',
  showPassword: true,
  margin: '0 0 16px 0',
};

const Security = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const { pwdPrenotazione, name, nome_salone } = useSelector(getActiveSalon());
  const salons = useSelector(getSalonsRaw());
  const activeSalonLabel = nome_salone || name || '';
  const showMultiSalonPasswordNotice = shouldShowMultiSalonPasswordNotice(salons);

  const initialValuesPassword = {
    oldPwd: '',
    newPwd: '',
    newPwdConfirm: '',
  };

  const validationSchemaPassword = {
    oldPwd: yup.string().required(t('security.validation.passwordRequired')),
    newPwd: yup.string().required(t('security.validation.newPasswordRequired')),
    newPwdConfirm: yup
      .string()
      .oneOf([yup.ref('newPwd'), null], t('security.validation.passwordsMustMatch'))
      .required(t('security.validation.confirmPasswordRequired')),
  };

  const initialValuesSecretWord = {
    pwdPrenotazione: pwdPrenotazione || '',
    nomeSalone: name || '',
  };

  const validationSchemaSecretWord = {
    pwdPrenotazione: yup.string(t('security.keywordSection.placeholder')),
  };

  return (
    <div
      className={clx(styles.salon_container, {
        [styles.salon_container_mobile]: isMobile,
      })}
    >
      <div className={styles.header}>
        <TitleSectionBack
          title={t('security.title')}
          description={t('security.description')}
        />
      </div>

      {showMultiSalonPasswordNotice ? (
        <div className={styles.password_admin_callout} role="note">
          <span className={styles.password_admin_callout_icon} aria-hidden="true">
            <AlertTriangle size={16} strokeWidth={2.2} />
          </span>
          <div className={styles.password_admin_callout_copy}>
            <span className={styles.password_admin_callout_label}>
              {t('security.passwordSection.adminPasswordNoticeTitle')}
            </span>
            <p>
              {t('security.passwordSection.activeSalonNotice', {
                salon: activeSalonLabel || '—',
              })}
            </p>
            <p>{t('security.passwordSection.adminPasswordNotice')}</p>
          </div>
        </div>
      ) : null}

      <Formik
        initialValues={initialValuesPassword}
        validationSchema={yup.object().shape(validationSchemaPassword)}
        enableReinitialize={true}
        onSubmit={(values, { resetForm }) => {
          const { newPwdConfirm, ...req } = values;
          dispatch(authActions.EDIT_SALON_PASSWORD.REQUEST(req, () => resetForm()));
        }}
      >
        {() => (
          <Form className={styles.salon_card}>
            <div className={styles.card_header}>
              <span className={styles.card_icon} aria-hidden="true">
                <KeyRound size={18} strokeWidth={2.2} />
              </span>
              <div className={styles.card_header_copy}>
                <div className={styles.section_title}>{t('security.passwordSection.title')}</div>
                <div className={styles.section_description}>
                  {t('security.passwordSection.description')}
                </div>
              </div>
            </div>
            <div className={styles.form_body}>
              <Field
                {...fieldProps}
                name={'oldPwd'}
                id={'oldPwd'}
                label={t('security.passwordSection.currentPassword')}
              />
              <Field
                {...fieldProps}
                name={'newPwd'}
                id={'newPwd'}
                label={t('security.passwordSection.newPassword')}
              />
              <Field
                {...fieldProps}
                name={'newPwdConfirm'}
                id={'newPwdConfirm'}
                label={t('security.passwordSection.confirmPassword')}
              />
              <div className={styles.actions}>
                <button type="submit" className={styles.confirm_button}>
                  {t('security.passwordSection.save')}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      <div className={styles.keyword_card}>
        <CollapsibleCard
          title={t('security.keywordSection.title')}
          description={t('security.keywordSection.description')}
          isOpenDefault={true}
        >
          <Formik
            initialValues={initialValuesSecretWord}
            validationSchema={yup.object().shape(validationSchemaSecretWord)}
            enableReinitialize={true}
            onSubmit={(values) => {
              dispatch(
                authActions.EDIT_SALON_INFO.REQUEST(values, () =>
                  dispatch(
                    toastActions.OPEN_TOAST.SUCCESS({
                      type: 'success',
                      props: { title: t('security.toast.keywordUpdated') },
                    }),
                  ),
                ),
              );
            }}
          >
            {() => (
              <Form className={styles.keyword_form}>
                <Field
                  name={'pwdPrenotazione'}
                  id={'pwdPrenotazione'}
                  component={TextField}
                  height={'50px'}
                  margin={'0 0 16px 0'}
                  placeholder={t('security.keywordSection.placeholder')}
                />
                <div className={styles.actions}>
                  <button type="submit" className={styles.confirm_button}>
                    {t('security.keywordSection.save')}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </CollapsibleCard>
      </div>
    </div>
  );
};

export default Security;
